import { crudPage } from '../ui/crud.js';
import { supabase } from '../config/supabaseClient.js';
import { badgeStatus } from '../ui/badges.js';
import { el, formatarNome, mascararMatricula } from '../lib/dom.js';
import { orBuscaPessoa } from '../lib/db.js';
import { abrirModal } from '../ui/modal.js';
import { toast } from '../lib/toast.js';
import { SECOES, permissoesPerfis, carregarPermissoes } from '../lib/permissoes.js';

const OPCOES_STATUS = [{ value: 'ATIVO', label: 'Ativo' }, { value: 'INATIVO', label: 'Inativo' }];
const OPCOES_PERFIL = [{ value: 'GESTOR', label: 'Gestor' }, { value: 'SUPERVISOR', label: 'Supervisor' }, { value: 'RECEP_ADM', label: 'Recepcionista administrativo' }, { value: 'RECEP_ASSIST', label: 'Recepcionista assistencial' }];
const rotuloPerfil = (p) => (OPCOES_PERFIL.find((o) => o.value === p)?.label || p);

async function buscarColaboradores(termo) {
  let q = supabase.from('colaborador').select('id, nome, matricula').eq('status', 'ATIVO');
  if (termo) q = q.or(orBuscaPessoa(termo));
  const { data } = await q.order('nome').limit(30);
  return (data || []).map((c) => ({ value: c.id, label: `${formatarNome(c.nome)} (mat. ${mascararMatricula(c.matricula)})` }));
}
const labelColaborador = (r) => (r.colaborador ? `${formatarNome(r.colaborador.nome)} (mat. ${mascararMatricula(r.colaborador.matricula)})` : '');

/** Acessos específicos de um usuário (override do perfil). */
async function abrirAcessos(u) {
  const nome = u.colaborador ? formatarNome(u.colaborador.nome) : 'usuário';
  if (u.perfil === 'GESTOR') {
    abrirModal({ titulo: `Acessos — ${nome}`, corpo: [el('p', { class: 'view__sub', text: 'Gestor tem acesso completo a tudo (não configurável).' })], acoes: [{ rotulo: 'Fechar', classe: 'btn' }] });
    return;
  }
  await carregarPermissoes();
  const mapaPerfil = permissoesPerfis()[u.perfil] || {};
  const base = (u.permissoes && typeof u.permissoes === 'object') ? u.permissoes : null;
  const efetivo = (s) => (base && (s in base)) ? !!base[s] : !!mapaPerfil[s];
  const estado = {};
  const corpo = [el('p', { class: 'view__sub', style: 'margin-top:0', text: `Perfil: ${rotuloPerfil(u.perfil)}. Marque o que ESTE usuário pode acessar. "Seguir o perfil" volta ao padrão.` })];
  let grupo = null;
  for (const s of SECOES) {
    estado[s.chave] = efetivo(s.chave);
    if (s.grupo !== grupo) { grupo = s.grupo; corpo.push(el('div', { class: 'cfg-grupo-lbl', text: grupo })); }
    const chk = el('input', { type: 'checkbox' });
    chk.checked = estado[s.chave];
    chk.addEventListener('change', () => { estado[s.chave] = chk.checked; });
    corpo.push(el('label', { class: 'cfg-item' }, [chk, el('div', {}, [el('div', { class: 'cfg-item__tit', text: s.rotulo })])]));
  }
  abrirModal({
    titulo: `Acessos — ${nome}`,
    corpo,
    acoes: [
      { rotulo: 'Cancelar', classe: 'btn' },
      { rotulo: 'Seguir o perfil', classe: 'btn', onClick: async () => {
        const { error } = await supabase.from('usuario').update({ permissoes: null }).eq('id', u.id);
        if (error) { toast('Erro: ' + error.message, 'erro'); return false; }
        u.permissoes = null; toast('Voltou a seguir o perfil.', 'sucesso'); return true;
      } },
      { rotulo: 'Salvar', classe: 'btn btn--primario', onClick: async () => {
        const { error } = await supabase.from('usuario').update({ permissoes: estado }).eq('id', u.id);
        if (error) { toast('Erro: ' + error.message, 'erro'); return false; }
        u.permissoes = estado; toast('Acessos do usuário salvos.', 'sucesso'); return true;
      } },
    ],
  });
}

/** Gera o comando SQL para ATIVAR o e-mail e ATIVAR o acesso do usuário. */
function guiaLogin(u) {
  const nome = u.colaborador ? formatarNome(u.colaborador.nome) : 'usuário';
  const email = el('input', { type: 'email', placeholder: 'e-mail da conta (Supabase Auth)', value: u.email || '' });
  const uid = el('input', { type: 'text', placeholder: 'User UID', value: u.auth_user_id || '' });
  const perfilSel = el('select', {}, OPCOES_PERFIL.map((o) => el('option', { value: o.value }, o.label)));
  perfilSel.value = u.perfil || 'RECEP_ADM';
  const sql = el('pre', { class: 'guia-sql' });
  const avisoUid = el('small', { class: 'campo__dica', style: 'color:var(--vermelho); display:none' });
  let uuidOk = false; // lido tambem pelo "Copiar comando" (nao anuncia sucesso com <UID> no comando)

  function gerar() {
    const e = (email.value || '').trim().replace(/'/g, "''");
    const bruto = (uid.value || '').trim();
    // Mesma armadilha do antigo campo do formulario: colar o NOME no lugar do UID. So entra no comando se tiver
    // formato de UID; senao fica o marcador <UID> e o aviso explica o que copiar.
    uuidOk = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bruto);
    const id = uuidOk ? bruto : '';
    avisoUid.textContent = 'Isto não parece um User UID. Copie o código da conta em Authentication → Users (formato xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx), não o nome da pessoa.';
    avisoUid.style.display = (bruto && !uuidOk) ? '' : 'none';
    const pf = perfilSel.value;
    sql.textContent =
      '-- 1) Ativar o e-mail (confirmar a conta)\n' +
      `update auth.users set email_confirmed_at = now() where email = '${e || '<email>'}';\n\n` +
      '-- 2) Ativar o acesso (vincular login + perfil + ativo)\n' +
      `update usuario set auth_user_id = '${id || '<UID>'}', perfil = '${pf}', status = 'ATIVO' where id = '${u.id}';`;
  }
  email.addEventListener('input', gerar);
  uid.addEventListener('input', gerar);
  perfilSel.addEventListener('change', gerar);
  gerar();

  const copiar = el('button', { type: 'button', class: 'btn', onClick: async () => {
    if (!(email.value || '').trim()) { toast('Preencha o e-mail da conta antes de copiar.', 'erro'); email.focus(); return; }
    if (!uuidOk) { toast('Cole primeiro o User UID da conta (Authentication → Users). O comando ainda está sem ele.', 'erro'); uid.focus(); return; }
    try { await navigator.clipboard.writeText(sql.textContent); toast('Comando copiado! Cole no SQL Editor do Supabase.', 'sucesso'); }
    catch { toast('Selecione o texto e copie (Ctrl+C).', 'info'); }
  } }, [el('i', { class: 'ph ph-copy' }), ' Copiar comando']);

  abrirModal({
    titulo: `Ativar acesso — ${nome}`,
    corpo: [
      el('p', { class: 'view__sub', style: 'margin-top:0', text: u.email
        ? 'O e-mail abaixo já foi puxado do cadastro do usuário. Passo 1: em Authentication → Add user, crie a conta com esse e-mail + uma senha. Passo 2: cole o User UID gerado. Passo 3: copie o comando e rode no SQL Editor.'
        : '⚠ Este usuário ainda não tem e-mail no cadastro. Feche, edite o usuário (lápis), preencha o e-mail e volte aqui — ele é puxado automaticamente.' }),
      el('div', { class: 'campo' }, [el('label', { text: 'E-mail da conta' }), email]),
      el('div', { class: 'campo' }, [el('label', { text: 'User UID' }), uid, avisoUid]),
      el('div', { class: 'campo' }, [el('label', { text: 'Perfil' }), perfilSel]),
      el('label', { class: 'view__sub', style: 'display:block; margin: 6px 0 4px', text: 'Comando gerado:' }),
      sql,
      el('div', { style: 'margin-top:10px' }, [copiar]),
    ],
    acoes: [{ rotulo: 'Fechar', classe: 'btn btn--primario' }],
  });
}

function redefinirPinUsuario(u) {
  const nome = u.colaborador ? formatarNome(u.colaborador.nome) : 'usuário';
  abrirModal({
    titulo: 'Redefinir PIN',
    corpo: [el('p', { class: 'view__sub', text: `Redefinir o PIN de ${nome} para 1234? A pessoa poderá trocar depois.` })],
    acoes: [
      { rotulo: 'Cancelar', classe: 'btn' },
      { rotulo: 'Redefinir para 1234', classe: 'btn btn--primario', onClick: async () => {
        const { data, error } = await supabase.rpc('gestor_redefinir_pin', { p_id: u.id, p_pin: '1234' });
        if (error || data !== true) { toast('Não foi possível redefinir (apenas GESTOR).', 'erro'); return false; }
        toast('PIN redefinido para 1234.', 'sucesso');
        return true;
      } },
    ],
  });
}

export const paginaUsuarios = crudPage({
  titulo: 'Usuários', singular: 'usuário',
  subtitulo: 'Quem usa o sistema (todos são operadores, com PIN). Recepcionista entra só por PIN. Para quem faz login com e-mail e senha: salve o usuário e depois use o botão "Ativar acesso".',
  tabela: 'usuario',
  select: 'id, perfil, status, auth_user_id, email, colaborador_id, permissoes, colaborador:colaborador_id(nome, matricula, empregador:empregador_id(empresa))',
  ordenarPor: 'created_at', asc: false,
  colunas: [
    { chave: 'colaborador', rotulo: 'Colaborador', ordenavel: false, render: (l) => l.colaborador ? formatarNome(l.colaborador.nome) : '—' },
    { chave: 'empresa', rotulo: 'Empresa', ordenavel: false, render: (l) => l.colaborador?.empregador?.empresa || '—' },
    { chave: 'perfil', rotulo: 'Perfil', render: (l) => rotuloPerfil(l.perfil) },
    { chave: 'status', rotulo: 'Status', render: (l) => badgeStatus(l.status) },
    { chave: '_acessos', rotulo: '', ordenavel: false, render: (l) => el('button', { class: 'btn', title: 'Acessos do usuário', 'aria-label': 'Acessos', onClick: () => abrirAcessos(l) }, [el('i', { class: 'ph ph-sliders' })]) },
    { chave: '_pin', rotulo: '', ordenavel: false, render: (l) => el('button', { class: 'btn', title: 'Redefinir PIN para 1234', 'aria-label': 'Redefinir PIN', onClick: () => redefinirPinUsuario(l) }, [el('i', { class: 'ph ph-key' })]) },
    { chave: '_login', rotulo: '', ordenavel: false, render: (l) => el('button', { class: 'btn', title: 'Ativar acesso (gerar comando)', 'aria-label': 'Ativar acesso', onClick: () => guiaLogin(l) }, [el('i', { class: 'ph ph-sign-in' })]) },
  ],
  buscarEm: ['perfil'],
  // Sem campo "Auth User ID" aqui: era texto livre num campo de codigo (uuid) e induzia a digitar o NOME da
  // pessoa (erro "invalid input syntax for type uuid", sugestao do Jorge em 22/09). O vinculo do login e feito
  // pelo botao "Ativar acesso" (guiaLogin), que ja gera o update de auth_user_id.
  campos: [
    { chave: 'colaborador_id', rotulo: 'Colaborador', tipo: 'busca', buscar: buscarColaboradores, labelDe: labelColaborador, obrigatorio: true, cheio: true },
    { chave: 'perfil', rotulo: 'Perfil', tipo: 'select', opcoes: OPCOES_PERFIL, obrigatorio: true },
    { chave: 'status', rotulo: 'Status', tipo: 'select', opcoes: OPCOES_STATUS, padrao: 'ATIVO' },
    { chave: 'email', rotulo: 'E-mail (só para quem faz login; em branco = entra só por PIN)', cheio: true },
  ],
});



